import React, {useState, useEffect} from 'react';
import {connect} from 'react-redux';
import * as actions from './orderStatusActions';
import {withFirebase} from '../Firebase';
import OrderStatus from './components/OrderStatus';
import moment from 'moment';
import {
  ORDER_COMPLETE,
  ORDER_DELIVERED,
  ORDER_REJECTED,
} from './orderStatusTypes';
import flattenProduct from '../../utils/flattenProduct';
import appConfig from '../../../app-config';

let OrderStatusContainer = ({
  orderHistory,
  firebase,
  orderStatusList,
  loadOrderStatus,
  removeAllOrderStatus,
  removeOrderStatus,
  sendReview,
  feedbackModalIsVisible,
  openFeedbackModal,
  closeFeedbackModal,
  orderSettingReducer,
  is_tip_enabled,
}) => {
  useEffect(() => {
    return () => {
      firebase.removeListeners();
      removeAllOrderStatus();
    };
  }, []);

  useEffect(() => {
    // start fresh by clearing all listners and statuses
    firebase.removeListeners();
    removeAllOrderStatus();
    // listen to each order
    orderHistory
      .filter(
        order =>
          order.config_products &&
          order.config_products.length &&
          order.firebase_uri
      )
      .forEach(order => {
        firebase.listen(order.firebase_uri, orderStatus => {
          if (orderStatus.config_product) {
            const formattedOrderStatus = formatOrderStatus(orderStatus);

            orderStatusIsValid(formattedOrderStatus)
              ? loadOrderStatus(formattedOrderStatus)
              : removeOrderStatus(formattedOrderStatus);
          } else {
            // how to handle this case?
          }
        });
      });
  }, [orderHistory]);

  return orderStatusList.length ? (
    <OrderStatus
      orderStatusList={orderStatusList}
      sendReview={sendReview}
      feedbackModalIsVisible={feedbackModalIsVisible}
      openFeedbackModal={openFeedbackModal}
      closeFeedbackModal={closeFeedbackModal}
      currency={orderStatusList[0].currency}
      is_tip_enabled={is_tip_enabled}
    />
  ) : null;
};

const mapStateToProps = state => ({
  orderHistory: state.orderHistoryReducer.orderHistory,
  orderStatusList: state.orderStatusReducer.orderStatusList
    .filter(order => order.config_product.length)
    .reduce((acc, orderStatus) => {
      if (
        state.locationsReducer.locations &&
        state.locationsReducer.locations.length
      ) {
        const location = state.locationsReducer.locations.filter(
          location => location.id == orderStatus.business_id
        );
        if (location && location.length && location[0].currency) {
          acc.push({...orderStatus, currency: location[0].currency});
        } else {
          acc.push({...orderStatus, currency: null});
        }
      }
      return acc;
    }, []),
  feedbackModalIsVisible: state.orderStatusReducer.feedbackModalIsVisible,
  orderSettingReducer: state.orderSettingReducer,
  is_tip_enabled: state.authReducer.appConfig.is_tip_enabled,
});

const formatOrderStatus = orderStatus => {
  let isFutureOrder = calculateIsFutureOrder(orderStatus);

  return {
    ...orderStatus,
    status:
      orderStatus.order_type === 'delivery' && orderStatus.status === 2
        ? 4
        : orderStatus.status,
    estimatedTime:
      orderStatus.order_date && orderStatus.estimated_time
        ? calculatePickupTime(
            orderStatus.order_date,
            orderStatus.estimated_time,
            isFutureOrder
          )
        : null,
    isFutureOrder,
    flatProducts: orderStatus.config_product.map(product => {
      let flatProduct = flattenProduct({
        ...product,
        options: product.options || [],
      });
      flatProduct = {
        ...flatProduct,
        singlePrice: flatProduct.prices[0].price,
        totalPrice: flatProduct.prices[0].price * flatProduct.quantity,
      };
      return {
        ...product,
        flatProduct,
        singlePrice: flatProduct.prices[0].price,
        totalPrice: flatProduct.prices[0].price * flatProduct.quantity,
      };
    }),
  };
};

const orderStatusIsValid = orderStatus => {
  if (orderStatus.status === ORDER_REJECTED) {
    return moment(orderStatus.order_date, 'YYYY-MM-DD HH:mm:ss').isAfter(
      moment().subtract(1, 'hour')
    );
  } else {
    if (orderStatus.is_future_order) {
      if (
        orderStatus.status === ORDER_COMPLETE ||
        orderStatus.status === ORDER_DELIVERED
      ) {
        return moment(orderStatus.order_date, 'YYYY-MM-DD HH:mm:ss').isAfter(
          moment().subtract(1, 'hour')
        );
      } else {
        return moment(orderStatus.order_date, 'YYYY-MM-DD HH:mm:ss').isAfter(
          moment().subtract(3, 'hour')
        );
      }
    } else {
      let readyTime;
      if ('delivery_tracking_url' in orderStatus) {
        readyTime = orderStatus.order_date;
      } else {
        const prepTimeInMilSeconds = new Date(
          '1970-01-01T' + orderStatus.min_prep_time + 'Z'
        ).getTime();
        const orderTime = new Date(
          moment(orderStatus.order_created_at, 'YYYY-MM-DD HH:mm:ss')
        );

        readyTime = new Date(orderTime.getTime() + prepTimeInMilSeconds);
      }

      if (
        orderStatus.status === ORDER_COMPLETE ||
        orderStatus.status === ORDER_DELIVERED
      ) {
        const momentTime = moment(readyTime).diff(
          moment().subtract(1, 'hour'),
          'minutes'
        );
        return momentTime >= 0 && !!momentTime;
      } else {
        const momentTime = moment(readyTime).diff(
          moment().subtract(3, 'hour'),
          'minutes'
        );

        return momentTime >= 0 && !!momentTime;
      }
    }
  }
};

const calculatePickupTime = (date, estimatedTime, isFutureOrder) => {
  const estimatedTimeArray = estimatedTime.split(':');
  const hours = parseInt(estimatedTimeArray[0]);
  const minutes = parseInt(estimatedTimeArray[1]);
  const seconds = parseInt(estimatedTimeArray[2]);

  const estimatedMinutes = hours * 60 + minutes + seconds / 60;

  let ret = moment(date, 'YYYY-MM-DD HH:mm:ss');

  // if future ordering, don't add estimate
  if (!isFutureOrder) {
    ret.add(estimatedMinutes, 'minutes');
  }

  return ret;
};

const calculateIsFutureOrder = orderStatus => {
  return (
    moment(orderStatus.order_date, 'YYYY-MM-DD HH:mm:ss').diff(
      moment(orderStatus.order_created_at, 'YYYY-MM-DD HH:mm:ss'),
      'seconds'
    ) > 10
  );
};

OrderStatusContainer = withFirebase(OrderStatusContainer);

export default connect(mapStateToProps, actions)(OrderStatusContainer);
